
import React, { useState, useEffect, useRef } from 'react';
import { Search, RefreshCw, Grid, Folder, FileText, Database, Terminal, Download, Upload, Lock, Unlock, X, Key, Settings, FileCode, ExternalLink, HelpCircle, Filter, List, ChevronLeft, ChevronRight, Layers, ShieldAlert, Shield, Brain, Scale, Circle, Triangle, Square, Hexagon, PlusCircle } from 'lucide-react';
import NeuralMap from '../components/NeuralMap';
import AtlasCard from '../components/AtlasCard';
import { AtlasCategory, AtlasEntry, VirtualDirectory } from '../types';
import { fetchAtlasData, getVirtualDirectories, exportDatabaseToJSON, downloadBatchTemplate, importDatabaseFromJSON, updateWordPressConfig, createEmptyEntry, saveEntryToWordPress } from '../services/dataService';
import { useSearchParams } from 'react-router-dom';

const Atlas: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // DATA STATE
  const [entries, setEntries] = useState<AtlasEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<AtlasEntry[]>([]);
  const [directories, setDirectories] = useState<VirtualDirectory[]>([]);
  
  // FILTER STATE
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<AtlasCategory | 'ALL'>('ALL');
  const [filterSeverity, setFilterSeverity] = useState<string | 'ALL'>('ALL');
  
  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; 

  // UI STATE
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'MAP' | 'ARCHIVE' | 'LIST'>('MAP');
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: 550 });
  const [showFilters, setShowFilters] = useState(true);
  
  // ADMIN STATE
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAdminHelp, setShowAdminHelp] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPass, setAuthPass] = useState('');

  // WP CONFIG STATE
  const [wpUrl, setWpUrl] = useState('https://www.cyberumanita.com');
  const [wpUser, setWpUser] = useState('');
  const [wpAppPass, setWpAppPass] = useState('');
  
  // State to trigger re-shuffling
  const [shuffleSeed, setShuffleSeed] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- HELPER: GET ICON FOR CATEGORY ---
  const getCategoryIcon = (category: string) => {
      switch(category) {
          case AtlasCategory.THREAT: return <Triangle size={14} className="text-red-600 fill-red-100" />;
          case AtlasCategory.DEFENSE: return <Square size={14} className="text-blue-600 fill-blue-100" />;
          case AtlasCategory.AI_MODEL: return <Hexagon size={14} className="text-green-600 fill-green-100" />;
          case AtlasCategory.REGULATION: return <Scale size={14} className="text-purple-600" />;
          default: return <Circle size={14} className="text-gray-400" />;
      }
  };

  // --- INITIAL LOAD ---
  const loadData = async () => {
      const data = await fetchAtlasData();
      setEntries(data);
      const dirs = await getVirtualDirectories();
      setDirectories(dirs);
  };

  useEffect(() => {
    loadData();
    
    const termParam = searchParams.get('term');
    if (termParam) {
        fetchAtlasData().then(data => {
            const exists = data.some(e => e.title.toLowerCase() === termParam.toLowerCase());
            if (exists) setSelectedTerm(termParam);
        });
    } else {
        setSelectedTerm(null);
    }

    const handleUpdate = () => loadData();
    window.addEventListener('atlas-update', handleUpdate);
    return () => window.removeEventListener('atlas-update', handleUpdate);

  }, [searchParams]);

  // --- FILTERING LOGIC ENGINE ---
  useEffect(() => {
    let results = entries;

    // 1. Text Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      results = results.filter(e => 
        e.title.toLowerCase().includes(q) || 
        e.tags.some(t => t.toLowerCase().includes(q))
      );
    }

    // 2. Category Filter
    if (filterCategory !== 'ALL') {
        results = results.filter(e => e.category === filterCategory);
    }

    // 3. Severity Filter
    if (filterSeverity !== 'ALL') {
        results = results.filter(e => e.severity === filterSeverity);
    }

    // 4. SORTING LOGIC FOR MAP
    if (viewMode === 'MAP' && !searchQuery && filterCategory === 'ALL' && filterSeverity === 'ALL') {
         // Default Cloud: Shuffle to show variety
         results = [...results].sort(() => 0.5 - Math.random());
    } else if (filterCategory !== 'ALL') {
         // Specific Category Filter: Sort by SEVERITY/IMPORTANCE to show most relevant nodes
         // This ensures that when filtering "Threats", we see Critical ones first, not just "A-Z"
         results = results.sort((a, b) => {
            const score = (sev?: string) => sev === 'Critical' ? 4 : sev === 'High' ? 3 : sev === 'Medium' ? 2 : 1;
            return score(b.severity) - score(a.severity);
         });
    } else {
         // List/Archive: Alphabetical
         results = results.sort((a, b) => a.title.localeCompare(b.title));
    }

    setFilteredEntries(results);
    setCurrentPage(1); 
  }, [searchQuery, entries, shuffleSeed, filterCategory, filterSeverity, viewMode]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEntries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: Math.min(window.innerWidth - 32, 1400),
        height: window.innerWidth < 768 ? 400 : 600
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleShuffle = () => setShuffleSeed(prev => prev + 1);

  // --- ADMIN LOGIC ---
  const handleAuthSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (authEmail === 'mind@cyberumanita.com' && authPass === 'G0G0M5ss1#1978') {
          setIsAdmin(true);
          setShowAuthModal(false);
          setAuthEmail('');
          setAuthPass('');
      } else {
          alert("ACCESS DENIED: Credenziali non valide.");
      }
  };

  const handleConfigSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (wpUrl && wpUser && wpAppPass) {
          updateWordPressConfig(wpUrl, wpUser, wpAppPass);
          setShowConfigModal(false);
          alert("Connessione WordPress Configurata per questa sessione.");
      }
  };

  const toggleAuthModal = () => {
      if (isAdmin) {
          setIsAdmin(false);
          setShowConfigModal(false);
          setShowAdminHelp(false);
      } else {
          setShowAuthModal(true);
      }
  };

  const handleCreateEntry = async () => {
      if(!confirm("Creare una nuova scheda vuota?")) return;
      const newEntry = createEmptyEntry();
      await saveEntryToWordPress(newEntry);
      await loadData();
      setSelectedTerm(newEntry.header.title);
  };

  const downloadPlugin = () => {
    const phpContent = `<?php
/**
 * Plugin Name: Cyberumanità Connector
 * Description: Connette il tuo sito WordPress all'App React "Cyberumanità".
 * Version: 2.0
 */
if ( ! defined( 'ABSPATH' ) ) exit; 
function cyber_enable_cors() {
    header( 'Access-Control-Allow-Origin: *' );
    header( 'Access-Control-Allow-Methods: POST, GET, OPTIONS, PUT, DELETE' );
    header( 'Access-Control-Allow-Credentials: true' );
    header( 'Access-Control-Allow-Headers: Authorization, Content-Type' );
    if ( 'OPTIONS' === $_SERVER['REQUEST_METHOD'] ) { status_header( 200 ); exit(); }
}
add_action( 'init', 'cyber_enable_cors' );
function cyber_register_atlas_cpt() {
    register_post_type( 'atlas_entry', array(
        'labels' => array('name' => 'Atlante DB', 'singular_name' => 'Scheda Atlante'),
        'public' => true, 'show_in_rest' => true, 'rest_base' => 'atlas_entry',
        'supports' => array( 'title', 'editor', 'custom-fields' )
    ));
}
add_action( 'init', 'cyber_register_atlas_cpt' );
function cyber_register_rest_fields() {
    register_rest_field( 'atlas_entry', 'atlas_full_data', array(
        'get_callback' => function( $object ) {
            $meta = get_post_meta( $object['id'], '_atlas_full_data', true );
            return !empty($meta) ? json_decode($meta) : null;
        },
        'update_callback' => function( $value, $object, $fieldName ) {
            return update_post_meta( $object->ID, '_atlas_full_data', json_encode($value) );
        },
        'schema' => null,
    ));
}
add_action( 'rest_api_init', 'cyber_register_rest_fields' );
add_action('rest_api_init', function () {
    register_rest_route('cyberumanita/v1', '/info', array(
        'methods' => 'GET',
        'callback' => function () {
            $logo_id = get_theme_mod( 'custom_logo' );
            $logo = wp_get_attachment_image_src( $logo_id , 'full' );
            return array('name' => get_bloginfo('name'), 'logo_url' => $logo ? $logo[0] : null);
        },
        'permission_callback' => '__return_true'
    ));
});
?>`;
      const blob = new Blob([phpContent], { type: 'text/php' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cyberumanita-connector.php';
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      alert("Plugin scaricato! Caricalo su WordPress.");
  };

  const handleImportClick = () => {
      fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
          try {
              const json = JSON.parse(event.target?.result as string);
              const result = await importDatabaseFromJSON(json);
              if (result.success) {
                  alert(result.message);
                  loadData();
              } else {
                  alert(`Errore Import: ${result.message}`);
              }
          } catch (err) {
              alert("Errore nella lettura del file. Assicurati sia un JSON valido.");
          }
      };
      reader.readAsText(file);
      e.target.value = '';
  };

  const handleCloseCard = () => {
    setSelectedTerm(null);
    setSearchParams(params => {
        params.delete('term');
        return params;
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-12 bg-paper text-black font-sans relative">
      
      {/* AUTH MODAL & CONFIG MODAL */}
      {showAuthModal && (
          <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white max-w-md w-full border-2 border-red-600 p-8 shadow-2xl relative">
                  <button onClick={() => setShowAuthModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black"><X size={20} /></button>
                  <div className="mb-6 flex flex-col items-center text-center">
                      <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center mb-4"><Lock size={24} /></div>
                      <h3 className="text-xl font-bold uppercase tracking-widest text-black">Restricted Area</h3>
                      <p className="text-xs text-gray-500 mt-2 font-mono">Inserire credenziali SysOp per sbloccare il database.</p>
                  </div>
                  <form onSubmit={handleAuthSubmit} className="space-y-4">
                      <div>
                          <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Identificativo Utente (Email)</label>
                          <input type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} className="w-full border border-gray-300 focus:border-black p-3 outline-none font-mono text-sm bg-white text-black" placeholder="user@cyberumanita.com" autoFocus />
                      </div>
                      <div>
                          <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Passphrase</label>
                          <input type="password" value={authPass} onChange={e => setAuthPass(e.target.value)} className="w-full border border-gray-300 focus:border-black p-3 outline-none font-mono text-sm bg-white text-black" placeholder="••••••••" />
                      </div>
                      <button type="submit" className="w-full bg-black text-white font-bold uppercase tracking-widest py-3 hover:bg-red-600 transition-colors mt-4 text-xs">Autenticazione</button>
                  </form>
              </div>
          </div>
      )}

      {showConfigModal && (
          <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white max-w-md w-full border border-gray-200 p-8 shadow-2xl relative">
                   <button onClick={() => setShowConfigModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black"><X size={20} /></button>
                   <div className="mb-6">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-black flex items-center gap-2"><Database size={16} /> Configurazione WordPress</h3>
                      <p className="text-xs text-gray-500 mt-2">Inserisci le credenziali del backend per la sincronizzazione.</p>
                  </div>
                   <form onSubmit={handleConfigSubmit} className="space-y-4">
                      <input type="text" value={wpUrl} onChange={e => setWpUrl(e.target.value)} className="w-full border border-gray-300 p-2 text-sm font-sans" placeholder="URL Sito" />
                      <input type="text" value={wpUser} onChange={e => setWpUser(e.target.value)} className="w-full border border-gray-300 p-2 text-sm font-sans" placeholder="Username WP" />
                      <input type="password" value={wpAppPass} onChange={e => setWpAppPass(e.target.value)} className="w-full border border-gray-300 p-2 text-sm font-sans" placeholder="App Password" />
                      <button type="submit" className="w-full bg-black text-white font-bold uppercase tracking-widest py-3 hover:bg-gray-800 transition-colors mt-2 text-xs">Salva Configurazione</button>
                   </form>
              </div>
          </div>
      )}

      <div className="max-w-[1600px] mx-auto px-4 sm:px-8">
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">
                <Database size={14} /> Database Centrale ({entries.length} Records)
            </div>
            <h2 className="text-6xl md:text-7xl font-serif mb-4">Atlante</h2>
            <p className="text-gray-500 font-light max-w-md">
              Il sistema nervoso della Cyberumanità. Nessun limite di importazione.
            </p>
          </div>
          
          <div className="flex gap-4 items-center">
             <div className="relative group">
                <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                    type="text"
                    placeholder="Search database..."
                    className="w-full md:w-64 bg-transparent border-b border-gray-300 pl-8 pr-4 py-2 text-lg focus:outline-none focus:border-black transition-colors font-serif placeholder:text-gray-300"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>
          </div>
        </div>

        {/* --- ADVANCED FILTER DECK --- */}
        <div className="bg-gray-50 border border-gray-200 p-4 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-200 pb-4 mb-4">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black">
                    <Filter size={14} /> Console Filtri
                </div>
                <div className="flex gap-2">
                     <button onClick={() => setViewMode('MAP')} className={`p-2 rounded ${viewMode === 'MAP' ? 'bg-black text-white' : 'bg-white text-gray-500 border border-gray-200'}`} title="Vista Neurale"><Grid size={16}/></button>
                     <button onClick={() => setViewMode('LIST')} className={`p-2 rounded ${viewMode === 'LIST' ? 'bg-black text-white' : 'bg-white text-gray-500 border border-gray-200'}`} title="Vista Lista Compatta"><List size={16}/></button>
                     <button onClick={() => setViewMode('ARCHIVE')} className={`p-2 rounded ${viewMode === 'ARCHIVE' ? 'bg-black text-white' : 'bg-white text-gray-500 border border-gray-200'}`} title="Vista Cartelle"><Folder size={16}/></button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* CATEGORY FILTER */}
                <div>
                    <label className="block text-[9px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Categoria</label>
                    <div className="flex flex-wrap gap-2">
                         <button onClick={() => setFilterCategory('ALL')} className={`px-2 py-1 text-[10px] uppercase font-bold border ${filterCategory === 'ALL' ? 'bg-black text-white border-black' : 'bg-white text-gray-500 border-gray-300'}`}>Tutti</button>
                         <button onClick={() => setFilterCategory(AtlasCategory.THREAT)} className={`px-2 py-1 text-[10px] uppercase font-bold border flex items-center gap-1 ${filterCategory === AtlasCategory.THREAT ? 'bg-red-50 border-red-500 text-red-600' : 'bg-white text-gray-500 border-gray-300'}`}><Triangle size={10} className="fill-red-500"/> Minacce</button>
                         <button onClick={() => setFilterCategory(AtlasCategory.DEFENSE)} className={`px-2 py-1 text-[10px] uppercase font-bold border flex items-center gap-1 ${filterCategory === AtlasCategory.DEFENSE ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-500 border-gray-300'}`}><Square size={10} className="fill-blue-500"/> Difesa</button>
                         <button onClick={() => setFilterCategory(AtlasCategory.AI_MODEL)} className={`px-2 py-1 text-[10px] uppercase font-bold border flex items-center gap-1 ${filterCategory === AtlasCategory.AI_MODEL ? 'bg-green-50 border-green-500 text-green-600' : 'bg-white text-gray-500 border-gray-300'}`}><Hexagon size={10} className="fill-green-500"/> AI</button>
                         <button onClick={() => setFilterCategory(AtlasCategory.REGULATION)} className={`px-2 py-1 text-[10px] uppercase font-bold border flex items-center gap-1 ${filterCategory === AtlasCategory.REGULATION ? 'bg-purple-50 border-purple-500 text-purple-600' : 'bg-white text-gray-500 border-gray-300'}`}><Scale size={10}/> Norme</button>
                    </div>
                </div>

                {/* SEVERITY FILTER */}
                <div>
                    <label className="block text-[9px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Livello Criticità</label>
                    <select 
                        value={filterSeverity} 
                        onChange={(e) => setFilterSeverity(e.target.value)} 
                        className="w-full bg-white border border-gray-300 p-1 text-xs font-bold uppercase outline-none"
                    >
                        <option value="ALL">Qualsiasi Livello</option>
                        <option value="Critical">Critical (Prio 1)</option>
                        <option value="High">High (Prio 2)</option>
                        <option value="Medium">Medium (Prio 3)</option>
                        <option value="Low">Low (Prio 4)</option>
                    </select>
                </div>
                
                {/* DATA COUNTER */}
                <div className="lg:col-span-2 flex justify-end items-end">
                    <div className="text-right">
                        <div className="text-[10px] font-bold uppercase text-gray-400">Risultati Filtrati</div>
                        <div className="text-3xl font-serif font-bold text-black">{filteredEntries.length}</div>
                    </div>
                </div>

            </div>
        </div>

        {/* ADMIN TOOLBAR */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-8">
            <div className="flex gap-2 items-center relative ml-auto">
                 {isAdmin ? (
                    <div className="flex items-center gap-2 animate-fade-in bg-red-50 px-2 py-1 rounded border border-red-100 relative">
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="application/json" className="hidden" />
                        
                        <button onClick={handleCreateEntry} className="flex items-center gap-2 text-[10px] font-bold uppercase text-white bg-green-600 hover:bg-black transition-colors px-3 py-1 border-r border-green-800 mr-2 rounded">
                            <PlusCircle size={14} /> NUOVO
                        </button>

                        <button onClick={downloadPlugin} className="flex items-center gap-2 text-[10px] font-bold uppercase text-white bg-red-600 hover:bg-black transition-colors px-3 py-1 border-r border-red-800 mr-2 rounded">
                            <FileCode size={14} /> WP CONNECTOR
                        </button>
                        <button onClick={() => setShowConfigModal(true)} className="flex items-center gap-2 text-[10px] font-bold uppercase text-red-800 hover:text-black transition-colors px-2">
                            <Settings size={14} />
                        </button>
                        <a href={`${wpUrl}/wp-admin/edit.php`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[10px] font-bold uppercase text-red-800 hover:text-black transition-colors px-2">
                             <ExternalLink size={14} /> WP ADMIN
                        </a>
                        <div className="w-px h-3 bg-red-200 mx-1"></div>
                        <button onClick={handleImportClick} className="flex items-center gap-2 text-[10px] font-bold uppercase text-red-800 hover:text-black px-2 py-1 transition-colors">
                            <Upload size={14} /> IMPORT
                        </button>
                        <button onClick={downloadBatchTemplate} className="flex items-center gap-2 text-[10px] font-bold uppercase text-red-800 hover:text-black transition-colors px-2">
                            <Download size={14} /> TMPL
                        </button>
                        <button onClick={exportDatabaseToJSON} className="text-[10px] font-mono text-red-800 hover:text-black hover:underline uppercase px-2">
                            EXPORT
                        </button>
                        <button onClick={() => setShowAdminHelp(!showAdminHelp)} className={`p-1 rounded-full ml-1 ${showAdminHelp ? 'bg-red-600 text-white' : 'text-red-600 hover:bg-red-100'}`}>
                            <HelpCircle size={14} />
                        </button>
                        {showAdminHelp && (
                            <div className="absolute top-full right-0 mt-2 w-72 bg-white border-2 border-red-600 shadow-xl p-4 z-50 animate-fade-in text-[10px] leading-relaxed">
                                <h4 className="font-bold text-red-600 uppercase mb-2 border-b border-gray-200 pb-1">Guida Comandi Admin</h4>
                                <ul className="space-y-2 text-gray-700">
                                    <li><strong className="text-black">NUOVO:</strong> Crea una scheda vuota e la apre per la modifica.</li>
                                    <li><strong className="text-black">WP CONNECTOR:</strong> Scarica il file .php da installare su WordPress per collegare il sito all'App.</li>
                                    <li><strong className="text-black">IMPORT DB:</strong> Carica un backup JSON locale (Max 20.000 entries testato).</li>
                                </ul>
                                <button onClick={() => setShowAdminHelp(false)} className="w-full mt-3 bg-gray-100 text-gray-500 font-bold py-1 hover:bg-black hover:text-white transition-colors">CHIUDI</button>
                            </div>
                        )}
                    </div>
                 ) : (
                    <span className="text-[9px] uppercase font-bold text-gray-300 select-none mr-2 flex items-center gap-1">
                        <Key size={10} /> Read Only
                    </span>
                 )}
                 <button onClick={toggleAuthModal} className={`p-2 rounded-full transition-colors ${isAdmin ? 'text-red-600 hover:bg-red-50' : 'text-gray-300 hover:text-gray-500'}`} title={isAdmin ? "Lock Admin Mode" : "Unlock Admin Access"}>
                    {isAdmin ? <Unlock size={14} /> : <Lock size={14} />}
                 </button>
            </div>
        </div>

        {/* --- MAIN VISUALIZATION AREA --- */}
        
        {viewMode === 'MAP' && (
            <div className="mb-16 opacity-0 animate-fade-in" style={{animationDelay: '0.1s'}}>
                <div className="border border-gray-100 bg-white shadow-sm relative">
                    <NeuralMap 
                        width={windowSize.width} 
                        height={windowSize.height} 
                        onNodeClick={setSelectedTerm}
                        entries={filteredEntries.slice(0, filterCategory === 'ALL' ? 150 : 300)} 
                    />
                    <div className="absolute top-4 right-4 flex items-center gap-2">
                        <button onClick={handleShuffle} className="p-2 bg-white border border-black hover:bg-black hover:text-white transition-colors text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                            <RefreshCw size={12} /> Shuffle Node
                        </button>
                    </div>
                </div>
            </div>
        )}

        {viewMode === 'LIST' && (
            <div className="mb-16 animate-fade-in">
                <div className="bg-white border border-gray-200 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase font-bold tracking-widest text-gray-500">
                                <th className="p-4">ID</th>
                                <th className="p-4">Titolo</th>
                                <th className="p-4">Categoria</th>
                                <th className="p-4">Severità</th>
                                <th className="p-4 text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {currentItems.map((entry) => (
                                <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                    <td className="p-4 font-mono text-xs text-gray-400">{entry.id.substring(0,8)}...</td>
                                    <td className="p-4 font-bold font-serif">{entry.title}</td>
                                    <td className="p-4">
                                        <span className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-gray-50 rounded text-gray-800 border border-gray-200 w-fit">
                                            {getCategoryIcon(entry.category)}
                                            {entry.category}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className={`text-[9px] uppercase font-bold ${entry.severity === 'Critical' ? 'text-red-600' : entry.severity === 'High' ? 'text-orange-500' : 'text-gray-500'}`}>
                                            {entry.severity || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => setSelectedTerm(entry.title)} className="text-[10px] font-bold uppercase underline hover:text-black text-gray-400">
                                            Apri
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {viewMode === 'ARCHIVE' && (
             <div className="mb-16 animate-fade-in">
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                    {currentItems.map(item => (
                        <div 
                        key={item.id}
                        onClick={() => setSelectedTerm(item.title)}
                        className="bg-white p-8 hover:bg-gray-50 cursor-pointer transition-all duration-300 group h-full flex flex-col relative overflow-hidden"
                        >
                        <div className="absolute top-0 left-0 w-1 h-full bg-black transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                        <div className="flex justify-between items-start mb-4">
                            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                {getCategoryIcon(item.category)}
                                {item.category}
                            </span>
                            {item.severity && item.severity !== 'Low' && (
                            <span className={`text-[10px] font-mono px-2 py-0.5 border ${item.severity === 'Critical' ? 'border-red-500 text-red-600' : 'border-gray-300 text-gray-500'}`}>
                                {item.severity}
                            </span>
                            )}
                        </div>
                        <h4 className="text-xl font-serif mb-3">{item.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-6 flex-grow font-light">
                            {item.shortDescription}
                        </p>
                        </div>
                    ))}
                 </div>
             </div>
        )}
        
        {/* PAGINATION */}
        {(viewMode === 'LIST' || viewMode === 'ARCHIVE') && totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8 mb-12">
                <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-200 hover:bg-black hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-black transition-colors rounded-full"
                >
                    <ChevronLeft size={20} />
                </button>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500">
                    Pagina {currentPage} di {totalPages}
                </span>
                <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-200 hover:bg-black hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-black transition-colors rounded-full"
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        )}

      </div>

      {selectedTerm && (
        <AtlasCard 
            term={selectedTerm} 
            onClose={handleCloseCard} 
            isAdmin={isAdmin}
            onDataChange={loadData}
        />
      )}
    </div>
  );
};

export default Atlas;
